import {useLayoutEffect, useRef, useState} from "react";
import type {ViewPort} from "@/encounter/model/ViewPort.ts";

const SMOOTHING_MS = 60;

/** Time-based smoothing: center moves linearly, zoom moves proportionally. */
export function interpolateViewport(current: ViewPort, target: ViewPort, elapsedMs: number): ViewPort {
    const weight = 1 - Math.exp(-Math.max(0, elapsedMs) / SMOOTHING_MS);
    const blend = (from: number, to: number) => from + (to - from) * weight;
    const blendSize = (from: number, to: number) => Math.exp(blend(
        Math.log(Math.max(from, Number.EPSILON)), Math.log(Math.max(to, Number.EPSILON)),
    ));
    return {
        centerX: blend(current.centerX, target.centerX),
        centerY: blend(current.centerY, target.centerY),
        visibleWidth: blendSize(current.visibleWidth, target.visibleWidth),
        visibleHeight: blendSize(current.visibleHeight, target.visibleHeight),
    };
}

function reachedTarget(current: ViewPort, target: ViewPort) {
    return Math.abs(current.centerX - target.centerX) < target.visibleWidth * 0.00001
        && Math.abs(current.centerY - target.centerY) < target.visibleHeight * 0.00001
        && Math.abs(Math.log(current.visibleWidth / target.visibleWidth)) < 0.00001
        && Math.abs(Math.log(current.visibleHeight / target.visibleHeight)) < 0.00001;
}

/** resetKey identifies the map/screen; never animate across a level switch or resize. */
export function useInterpolatedPlayerViewport(target: ViewPort, resetKey: string): ViewPort {
    const [display, setDisplay] = useState({key: resetKey, viewport: target});
    const current = useRef({key: resetKey, viewport: target});
    const reset = display.key !== resetKey;
    if (reset) setDisplay({key: resetKey, viewport: target});

    useLayoutEffect(() => {
        if (current.current.key !== resetKey) current.current = {key: resetKey, viewport: target};
        let previousTime = performance.now();
        let frame: number | undefined;
        const tick = (time: number) => {
            const next = interpolateViewport(current.current.viewport, target, time - previousTime);
            previousTime = time;
            const done = reachedTarget(next, target);
            current.current = {key: resetKey, viewport: done ? target : next};
            setDisplay(current.current);
            if (!done) frame = requestAnimationFrame(tick);
        };
        if (!reachedTarget(current.current.viewport, target)) frame = requestAnimationFrame(tick);
        return () => { if (frame !== undefined) cancelAnimationFrame(frame); };
    }, [target, resetKey]);

    return reset ? target : display.viewport;
}
